import React, { useState, useEffect } from 'react';
import { ProjectRow } from '../../../lib/types';
import { useAuth } from '../../hooks/useAuth';

interface IdeaCard extends ProjectRow {
  voteCount?: number;
  upVotes?: number;
  downVotes?: number;
  commentCount?: number;
  comments?: any[];
  impactScore?: number;
  effortScore?: number;
  assessmentScore?: number;
}

interface DetailPanelData {
  idea: IdeaCard;
  votes: { upVotes: number; downVotes: number };
  publicComments: any[];
  privateComments: any[];
  evidence: any[];
  assessment?: any;
  impactScore?: number;
  effortScore?: number;
  history?: any[];
}

interface ActionLog {
  timestamp: string;
  action: string;
  performedByEmail: string;
  performedByName: string;
}

export default function DiscoveryKanban() {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState<{ [key: string]: IdeaCard[] }>({
    NewIdea: [],
    GatheringFeedback: [],
    ReadyForGovernance: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState<DetailPanelData | null>(null);
  const [impactScore, setImpactScore] = useState(0);
  const [effortScore, setEffortScore] = useState(0);
  const [notes, setNotes] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newLink, setNewLink] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectCategory, setRejectCategory] = useState('other');

  const statuses = [
    { key: 'NewIdea', label: '💡 New Ideas', color: '#fafafa' },
    { key: 'GatheringFeedback', label: '🔄 Gathering Feedback', color: '#f5f5f5' },
    { key: 'ReadyForGovernance', label: '✅ Ready for Governance', color: '#f0f0f0' },
  ];

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const res = await fetch('/projects');
      const data = await res.json();

      if (data.success) {
        const discoveryIdeas = (data.data || []).filter((p: ProjectRow) =>
          ['NewIdea', 'GatheringFeedback', 'ReadyForGovernance'].includes(p.Status)
        );

        const grouped: { [key: string]: IdeaCard[] } = {
          NewIdea: [],
          GatheringFeedback: [],
          ReadyForGovernance: [],
        };

        for (const idea of discoveryIdeas) {
          grouped[idea.Status].push(idea);
        }

        setIdeas(grouped);
      }
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIdeaClick = async (idea: IdeaCard) => {
    try {
      // Fetch votes, comments (with private flag), evidence, assessment, and action history
      const [votesRes, commentsRes, evidenceRes, assessmentRes, historyRes] = await Promise.all([
        fetch(`/projects/${idea.ProjectId}/votes`),
        fetch(`/projects/${idea.ProjectId}/comments?includePrivate=true`),
        fetch(`/projects/${idea.ProjectId}/evidence`).catch(() => ({ json: async () => ({ data: [] }) })),
        fetch(`/projects/${idea.ProjectId}/assessment`),
        fetch(`/projects/${idea.ProjectId}/action-log`).catch(() => ({ json: async () => ({ data: [] }) })),
      ]);

      const votesData = await votesRes.json();
      const commentsData = await commentsRes.json();
      const evidenceData = await evidenceRes.json();
      const assessmentData = await assessmentRes.json();
      const historyData = await historyRes.json();

      const allComments = commentsData.data || [];
      const publicComments = allComments.filter((c: any) => !c.IsPrivate);
      const privateComments = allComments.filter((c: any) => c.IsPrivate);

      const assessment = assessmentData.data;
      const history = (historyData.data || []).sort((a: any, b: any) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setSelectedIdea({
        idea,
        votes: {
          upVotes: votesData.data?.UpVotes || 0,
          downVotes: votesData.data?.DownVotes || 0,
        },
        publicComments,
        privateComments,
        evidence: evidenceData.data || [],
        assessment,
        history,
        impactScore: assessment?.Impact || idea.impactScore || 0,
        effortScore: assessment?.Effort || idea.effortScore || 0,
      });

      setImpactScore(assessment?.Impact || idea.impactScore || 0);
      setEffortScore(assessment?.Effort || idea.effortScore || 0);
      setNotes('');
    } catch (error) {
      console.error('Failed to load idea details:', error);
    }
  };

  const handleUpdateScores = async () => {
    if (!selectedIdea || !user) return;

    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}/assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          impact: impactScore,
          effort: effortScore,
          assessedByEmail: user.Email,
          assessedByName: user.Name,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to update scores:', error);
    }
  };

  const logAction = async (projectId: string, action: string) => {
    if (!user || selectedIdea?.idea.WorkType === 'NonProduct') {
      // Don't log actions for NonProduct (BAU) ideas
      return;
    }

    try {
      await fetch(`/projects/${projectId}/action-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          performedByEmail: user.Email,
          performedByName: user.Name,
        }),
      }).catch(() => {
        // Endpoint may not exist yet, continue anyway
      });
    } catch (error) {
      console.error('Failed to log action:', error);
    }
  };

  const handleBeginGatheringFeedback = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'GatheringFeedback' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Begin Gathering Feedback');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleFastTrack = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'ReadyForGovernance' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Fast-track to Governance');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to fast-track:', error);
    }
  };

  const handleReadyForGovernance = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'ReadyForGovernance' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Ready for Governance');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to mark ready for governance:', error);
    }
  };

  const handleMoveToBelowTheLine = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'BelowTheLine' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Move to Below-the-Line (Fast Path)');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to move to below-the-line:', error);
    }
  };

  const handleMoveToAboveTheLine = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'AboveTheLine' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Gate 2a - Approve for Business Case & Planning');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to move to above-the-line:', error);
    }
  };

  const handleMoveToReadyForDelivery = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'ReadyForDelivery' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Gate 2b - Portfolio Sign-Off to Proceed');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to move to ready for delivery:', error);
    }
  };

  const handleReturnForGathering = async () => {
    if (!selectedIdea || !user) return;
    try {
      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: 'GatheringFeedback' }),
      });

      const data = await response.json();
      if (data.success) {
        await logAction(selectedIdea.idea.ProjectId, 'Return for More Gathering');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to return for gathering:', error);
    }
  };

  const handleAddComment = async () => {
    if (!selectedIdea || !user || !newComment.trim()) return;

    try {
      await fetch(`/projects/${selectedIdea.idea.ProjectId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorEmail: user.Email,
          authorName: user.Name,
          content: newComment,
          isPrivate: true, // Internal comment
        }),
      });

      setNewComment('');
      // Reload idea details
      handleIdeaClick(selectedIdea.idea);
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleAddLink = async () => {
    if (!selectedIdea || !user || !newLink.trim()) return;

    try {
      await fetch(`/projects/${selectedIdea.idea.ProjectId}/evidence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Reference Link',
          content: newLink,
          addedByEmail: user.Email,
          addedByName: user.Name,
        }),
      });

      setNewLink('');
      // Reload idea details
      handleIdeaClick(selectedIdea.idea);
    } catch (error) {
      console.error('Failed to add link:', error);
    }
  };

  const handleRejectArchive = async () => {
    if (!selectedIdea) return;
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!selectedIdea || !user || !rejectReason.trim()) return;

    try {
      // Log the rejection with reason
      await logAction(
        selectedIdea.idea.ProjectId,
        `Reject & Archive - ${rejectCategory}: ${rejectReason}`
      );

      const response = await fetch(`/projects/${selectedIdea.idea.ProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Status: 'Rejected',
          ArchivedByEmail: user.Email,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowRejectModal(false);
        setRejectReason('');
        setRejectCategory('other');
        setSelectedIdea(null);
        fetchIdeas();
      }
    } catch (error) {
      console.error('Failed to reject:', error);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading ideas...</div>;
  }

  return (
    <div style={{ padding: '2rem', backgroundColor: '#fafafa', minHeight: '100vh' }}>
      <h2 style={{ marginBottom: '2.5rem', fontSize: '1.8rem', fontWeight: '700', color: '#000', letterSpacing: '0px' }}>Ideas Lab</h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '2rem',
        }}
      >
        {statuses.map(status => (
          <div
            key={status.key}
            style={{
              backgroundColor: status.color,
              borderRadius: '8px',
              padding: '1.5rem',
              minHeight: '600px',
            }}
          >
            <h3 style={{ marginBottom: '1.25rem', marginTop: 0, fontSize: '1rem', fontWeight: '600', color: '#333' }}>
              {status.label}
              <span style={{ fontSize: '0.85rem', color: '#999', marginLeft: '0.5rem', fontWeight: '500' }}>
                ({ideas[status.key].length})
              </span>
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {ideas[status.key].map(idea => (
                <div
                  key={idea.ProjectId}
                  onClick={() => handleIdeaClick(idea)}
                  style={{
                    backgroundColor: 'white',
                    padding: '1rem',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    border: '1px solid #d0d0d0',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                  onMouseOver={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                    (e.currentTarget as HTMLElement).style.borderColor = '#999';
                  }}
                  onMouseOut={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                    (e.currentTarget as HTMLElement).style.borderColor = '#d0d0d0';
                  }}
                >
                  <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.95rem', fontWeight: '600', color: '#000', lineHeight: '1.4' }}>{idea.Title}</h4>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>
                    {idea.Description?.substring(0, 100)}...
                  </p>
                  {(idea.impactScore || idea.effortScore || idea.assessmentScore) && (
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                      {idea.impactScore && <span style={{ color: '#666' }}>💥 {idea.impactScore}</span>}
                      {idea.effortScore && <span style={{ color: '#666' }}>⚡ {idea.effortScore}</span>}
                      {idea.assessmentScore && <span style={{ color: '#999', fontWeight: '500' }}>📊 {idea.assessmentScore.toFixed(1)}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Detail Panel - Right Slide */}
      {selectedIdea && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out',
          }}
          onClick={() => setSelectedIdea(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              width: '100%',
              maxWidth: '600px',
              height: '100vh',
              overflowY: 'auto',
              padding: '2.5rem',
              boxShadow: '-2px 0 8px rgba(0,0,0,0.15)',
              animation: 'slideInRight 0.3s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedIdea(null)}
              style={{
                position: 'absolute',
                top: '1.5rem',
                right: '1.5rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#999',
              }}
            >
              ✕
            </button>

            {/* Status Badge */}
            <div style={{ marginBottom: '1rem', display: 'inline-block' }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '0.4rem 0.85rem',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '3px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  letterSpacing: '0.5px',
                }}
              >
                {selectedIdea.idea.Status === 'NewIdea'
                  ? '💡 New Idea'
                  : selectedIdea.idea.Status === 'GatheringFeedback'
                  ? '🔄 Gathering Feedback'
                  : '✅ Ready for Governance'}
              </span>
            </div>

            {/* Title & Basic Info */}
            <h2 style={{ marginTop: '0.5rem', marginBottom: '1rem', fontSize: '1.8rem' }}>
              {selectedIdea.idea.Title}
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '2rem' }}>
              Submitted by <strong>{selectedIdea.idea.CreatedByName}</strong> • Domain: <strong>{selectedIdea.idea.Domain}</strong>
            </p>

            {/* Description */}
            <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #eee' }}>
              <h3 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1rem' }}>Description</h3>
              <p style={{ color: '#444', lineHeight: 1.8, margin: 0 }}>{selectedIdea.idea.Description}</p>
            </div>

            {/* Community Response */}
            <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>Community Response</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1.25rem', backgroundColor: '#ffffff', border: '1px solid #d0d0d0', borderRadius: '3px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#000' }}>
                    👍 {selectedIdea.votes.upVotes}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', fontWeight: '500' }}>Likes</div>
                </div>
                <div style={{ padding: '1.25rem', backgroundColor: '#ffffff', border: '1px solid #d0d0d0', borderRadius: '3px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#000' }}>
                    👎 {selectedIdea.votes.downVotes}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', fontWeight: '500' }}>Dislikes</div>
                </div>
              </div>
            </div>

            {/* All Comments (Public + Community Feedback) */}
            {(selectedIdea.publicComments.length > 0 || selectedIdea.privateComments.length > 0) && (
              <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>
                  Feedback & Discussion ({selectedIdea.publicComments.length + selectedIdea.privateComments.length})
                </h3>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {selectedIdea.publicComments.map((comment, idx) => (
                    <div key={`public-${idx}`} style={{ padding: '0.85rem', backgroundColor: '#fafafa', border: '1px solid #d0d0d0', marginBottom: '0.75rem', borderRadius: '3px', fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.25rem' }}>
                        <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>{comment.AuthorName || 'Community'}</p>
                        <span style={{ fontSize: '0.75rem', color: '#999', backgroundColor: '#f0f0f0', padding: '0.2rem 0.5rem', borderRadius: '2px' }}>
                          Public
                        </span>
                      </div>
                      <p style={{ margin: 0, color: '#666', lineHeight: '1.5' }}>{comment.Content || comment.CommentText}</p>
                    </div>
                  ))}
                  {selectedIdea.privateComments.map((comment, idx) => (
                    <div key={`private-${idx}`} style={{ padding: '0.85rem', backgroundColor: '#fafafa', border: '1px solid #d0d0d0', marginBottom: '0.75rem', borderRadius: '3px', fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.25rem' }}>
                        <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>{comment.AuthorName}</p>
                        <span style={{ fontSize: '0.75rem', color: '#999', backgroundColor: '#f0f0f0', padding: '0.2rem 0.5rem', borderRadius: '2px' }}>
                          Internal
                        </span>
                      </div>
                      <p style={{ margin: 0, color: '#666', lineHeight: '1.5' }}>{comment.Content || comment.CommentText}</p>
                    </div>
                  ))}
                </div>
                {selectedIdea.idea.Status === 'GatheringFeedback' && (
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add an internal note or observation..."
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '0.75rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '3px',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        marginBottom: '0.5rem',
                        backgroundColor: '#ffffff',
                      }}
                    />
                    <button
                      onClick={handleAddComment}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#333',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                      }}
                    >
                      Add Comment
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Stage-Specific Content */}
            {selectedIdea.idea.Status === 'NewIdea' && (
              <div style={{ paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0', marginBottom: '2rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>Next Steps</h3>
                <p style={{ color: '#666', margin: 0, lineHeight: '1.6' }}>
                  This idea is ready for investigation. Begin gathering feedback, collect evidence, and assess impact and effort.
                </p>
              </div>
            )}

            {(selectedIdea.idea.Status === 'GatheringFeedback' ||
              selectedIdea.idea.Status === 'ReadyForGovernance') && (
              <>
                {/* Assessment Scores */}
                <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>Assessment</h3>

                  {selectedIdea.idea.Status === 'GatheringFeedback' ? (
                    <>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#000', fontSize: '0.95rem' }}>
                          Impact (1-5)
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="5"
                          value={impactScore}
                          onChange={(e) => setImpactScore(parseInt(e.target.value))}
                          style={{ width: '100%', cursor: 'pointer' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#999', marginTop: '0.4rem' }}>
                          <span>Low</span>
                          <span style={{ fontWeight: '700', color: '#333' }}>{impactScore}</span>
                          <span>High</span>
                        </div>
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#000', fontSize: '0.95rem' }}>
                          Effort (1-5)
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="5"
                          value={effortScore}
                          onChange={(e) => setEffortScore(parseInt(e.target.value))}
                          style={{ width: '100%', cursor: 'pointer' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#999', marginTop: '0.4rem' }}>
                          <span>Low</span>
                          <span style={{ fontWeight: '700', color: '#333' }}>{effortScore}</span>
                          <span>High</span>
                        </div>
                      </div>

                      {impactScore > 0 && effortScore > 0 && (
                        <div
                          style={{
                            padding: '1.25rem',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '3px',
                            border: '1px solid #d0d0d0',
                          }}
                        >
                          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.3rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Score
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#000' }}>
                            {((Math.pow(impactScore, 2) * 2) - effortScore).toFixed(1)}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.35rem' }}>
                            (Impact² × 2) - Effort
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      style={{
                        padding: '1.5rem',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '3px',
                        border: '1px solid #d0d0d0',
                      }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Impact
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#000' }}>
                            {impactScore}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Effort
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#000' }}>
                            {effortScore}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '3px', padding: '0.75rem', border: '1px solid #d0d0d0' }}>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Score
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#000' }}>
                            {((Math.pow(impactScore, 2) * 2) - effortScore).toFixed(1)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Private Comments (only for GatheringFeedback & ReadyForGovernance) */}
            {selectedIdea.idea.Status !== 'NewIdea' && (
              <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>Internal Notes</h3>
                {selectedIdea.privateComments.length > 0 ? (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '1rem' }}>
                    {selectedIdea.privateComments.map((comment, idx) => (
                      <div key={idx} style={{ padding: '0.85rem', backgroundColor: '#fafafa', border: '1px solid #d0d0d0', marginBottom: '0.75rem', borderRadius: '3px', fontSize: '0.9rem' }}>
                        <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#333' }}>{comment.AuthorName}</p>
                        <p style={{ margin: 0, color: '#666', lineHeight: '1.5' }}>{comment.Content || comment.CommentText}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#999', fontSize: '0.9rem', margin: 0, marginBottom: '1rem' }}>
                    No internal notes yet
                  </p>
                )}
                {selectedIdea.idea.Status === 'GatheringFeedback' && (
                  <textarea
                    placeholder="Add an internal note..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '0.85rem',
                      border: '1px solid #d0d0d0',
                      borderRadius: '3px',
                      fontSize: '0.9rem',
                      fontFamily: 'inherit',
                      backgroundColor: '#ffffff',
                      color: '#333',
                    }}
                  />
                )}
              </div>
            )}

            {/* Evidence & Links (only for GatheringFeedback & ReadyForGovernance) */}
            {selectedIdea.idea.Status !== 'NewIdea' && (
              <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>Evidence & Links</h3>
                {selectedIdea.evidence && selectedIdea.evidence.length > 0 ? (
                  <div style={{ marginBottom: '1rem' }}>
                    {selectedIdea.evidence.map((item, idx) => (
                      <div key={idx} style={{ padding: '0.85rem', backgroundColor: '#fafafa', border: '1px solid #d0d0d0', marginBottom: '0.5rem', borderRadius: '3px', fontSize: '0.9rem' }}>
                        <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#333' }}>
                          {item.Title || 'Reference Link'}
                        </p>
                        {item.Content?.startsWith('http') ? (
                          <a
                            href={item.Content}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#333', textDecoration: 'underline', fontSize: '0.85rem' }}
                          >
                            {item.Content}
                          </a>
                        ) : (
                          <p style={{ margin: 0, color: '#666', fontSize: '0.85rem', lineHeight: '1.5' }}>{item.Content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#999', fontSize: '0.9rem', margin: '0 0 1rem 0' }}>
                    No links or evidence added yet
                  </p>
                )}
                {selectedIdea.idea.Status === 'GatheringFeedback' && (
                  <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>
                    <input
                      type="url"
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      placeholder="Paste link (https://...)"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '3px',
                        fontSize: '0.9rem',
                        marginBottom: '0.5rem',
                        backgroundColor: '#ffffff',
                        color: '#333',
                        boxSizing: 'border-box',
                      }}
                    />
                    <button
                      onClick={handleAddLink}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#333',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                      }}
                    >
                      Add Link
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Action History */}
            {selectedIdea?.history && selectedIdea.history.length > 0 && (
              <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#000' }}>Action History</h3>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {selectedIdea.history.map((entry, idx) => (
                    <div key={idx} style={{ padding: '0.85rem', backgroundColor: '#fafafa', border: '1px solid #e0e0e0', marginBottom: '0.75rem', borderRadius: '3px', fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>{entry.action}</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>
                          {new Date(entry.timestamp).toLocaleDateString()} {new Date(entry.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                        by {entry.performedByName} ({entry.performedByEmail})
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', paddingTop: '1rem' }}>
              {selectedIdea.idea.Status === 'NewIdea' && (
                <>
                  <button
                    onClick={handleBeginGatheringFeedback}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#333',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    🔄 Begin Gathering Feedback
                  </button>
                  <button
                    onClick={handleFastTrack}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ⚡ Fast-track to Governance
                  </button>
                  <button
                    onClick={handleRejectArchive}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: '1px solid #d0d0d0',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ✕ Reject & Archive
                  </button>
                </>
              )}

              {selectedIdea.idea.Status === 'GatheringFeedback' && (
                <>
                  <button
                    onClick={handleUpdateScores}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    💾 Save Assessment
                  </button>
                  <button
                    onClick={handleReadyForGovernance}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#333',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ✅ Ready for Governance
                  </button>
                  <button
                    onClick={handleFastTrack}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ⚡ Fast-track to Governance
                  </button>
                  <button
                    onClick={handleRejectArchive}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: '1px solid #d0d0d0',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ✕ Reject & Archive
                  </button>
                </>
              )}

              {selectedIdea.idea.Status === 'ReadyForGovernance' && (
                <>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#666', fontWeight: '600', textTransform: 'uppercase' }}>
                    Governance Decision
                  </div>
                  <button
                    onClick={handleMoveToBelowTheLine}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                      marginBottom: '0.5rem',
                    }}
                  >
                    ⚡ Below-the-Line (Fast Path)
                  </button>
                  <button
                    onClick={handleMoveToAboveTheLine}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#333',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                      marginBottom: '0.5rem',
                    }}
                  >
                    📋 Gate 2a - Approve Business Case
                  </button>
                  <button
                    onClick={handleReturnForGathering}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                      marginBottom: '0.5rem',
                    }}
                  >
                    🔄 Return for More Gathering
                  </button>
                  <button
                    onClick={handleRejectArchive}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: '1px solid #d0d0d0',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ✕ Reject & Archive
                  </button>
                </>
              )}

              {selectedIdea.idea.Status === 'AboveTheLine' && (
                <>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#666', fontWeight: '600', textTransform: 'uppercase' }}>
                    Business Case & Planning Phase
                  </div>
                  <button
                    onClick={handleMoveToReadyForDelivery}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                      marginBottom: '0.5rem',
                    }}
                  >
                    ✅ Gate 2b - Portfolio Sign-Off
                  </button>
                  <button
                    onClick={handleRejectArchive}
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: '1px solid #d0d0d0',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    ✕ Reject & Archive
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowRejectModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '3px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '700', color: '#000' }}>
              Reject & Archive Idea
            </h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#000', fontSize: '0.95rem' }}>
                Category
              </label>
              <select
                value={rejectCategory}
                onChange={(e) => setRejectCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d0d0d0',
                  borderRadius: '3px',
                  fontSize: '0.9rem',
                  backgroundColor: '#ffffff',
                  color: '#333',
                }}
              >
                <option value="duplicate">Duplicate</option>
                <option value="out-of-scope">Out of Scope</option>
                <option value="insufficient-info">Insufficient Information</option>
                <option value="not-feasible">Not Feasible</option>
                <option value="low-priority">Low Priority</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#000', fontSize: '0.95rem' }}>
                Reason for Rejection *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this idea is being rejected..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  border: '1px solid #d0d0d0',
                  borderRadius: '3px',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#333',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim()}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: rejectReason.trim() ? '#333' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: rejectReason.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                }}
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setRejectCategory('other');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #d0d0d0',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  );
}
